/**
 * router/routes.js
 *
 * Daftar semua route Portal IHC, dikelompokkan per area:
 *   - Authentication Routes (Public): login, register, forgot-password
 *   - Main Application (Protected):
 *       • Dashboard
 *       • Master Data: access, analysis-form, branch, medicine, intervention,
 *                      assessment, service-salary, bank
 *       • Settings:    reason, transportation, payroll-schedule,
 *                      attendance-schedule
 *
 * Konvensi:
 * - Semua route protected wajib pakai `meta.authRequired: true`.
 * - `meta.title` wajib diisi (dipakai untuk update document.title oleh
 *   beforeResolve di router/index.js).
 * - Component pakai dynamic import (`() => import(...)`) supaya code-splitting
 *   otomatis per route.
 *
 * Cara tambah route baru: ikuti pattern existing, kelompokkan di bawah
 * komentar section yang sesuai (MASTER DATA / SETTINGS / dst).
 */
export default [
  // ==============================
  // Authentication Routes (Public)
  // ==============================
  {
    path: "/login",
    name: "login",
    component: () => import("../views/account/login.vue"),
    meta: {
      title: "Login",
    },
  },
  {
    path: "/register",
    name: "Register",
    component: () => import("../views/account/register.vue"),
    meta: {
      title: "Register",
    },
  },
  {
    path: "/forgot-password",
    name: "Forgot password",
    component: () => import("../views/account/forgot-password.vue"),
    meta: {
      title: "Forgot Password",
    },
  },

  // ==============================
  // Main Application Routes (Protected)
  // ==============================
  {
    path: "/",
    name: "default",
    meta: {
      title: "Dashboard",
      authRequired: true,
    },
    component: () => import("../views/dashboard/index.vue"),
  },

  /* -------------------------------------------------------------------------- */
  /*                                 MASTER DATA                                */
  /* -------------------------------------------------------------------------- */
  {
    path: "/master-data/access",
    name: "access",
    meta: {
      title: "Hak Akses",
      authRequired: true,
    },
    component: () => import("../views/master-data/access/index.vue"),
  },
  {
    path: "/master-data/access/role/tambah",
    name: "role-tambah",
    meta: {
      title: "Tambah Role",
      authRequired: true,
    },
    component: () => import("../views/master-data/access/components/role/FormRole.vue"),
  },
  {
    path: "/master-data/access/role/edit/:id",
    name: "role-edit",
    meta: {
      title: "Edit Role",
      authRequired: true,
    },
    component: () => import("../views/master-data/access/components/role/FormRole.vue"),
  },
  {
    path: "/master-data/analysis-form",
    name: "analysis-form",
    meta: {
      title: "Form Analisa",
      authRequired: true,
    },
    component: () => import("../views/master-data/analysis-form/index.vue"),
  },
  {
    path: "/master-data/branch",
    name: "branch",
    meta: {
      title: "Cabang",
      authRequired: true,
    },
    component: () => import("../views/master-data/branch/index.vue"),
  },
  {
    path: '/master-data/medicine',
    name: 'medicine',
    meta: {
      title: 'Obat',
      authRequired: true,
    },
    component: () => import('../views/master-data/medicine/index.vue'),
  },
  {
    path: '/master-data/intervention',
    name: 'intervention',
    meta: {
      title: 'Intervensi',
      authRequired: true,
    },
    component: () => import('../views/master-data/intervention/index.vue'),
  },
  {
    path: '/master-data/assessment',
    name: 'assessment',
    meta: {
      title: 'Asesmen',
      authRequired: true,
    },
    component: () => import('../views/master-data/assessment/index.vue'),
  },
  {
    path: '/master-data/service-salary',
    name: 'service-salary',
    meta: {
      title: 'Gaji Layanan',
      authRequired: true,
    },
    component: () => import('../views/master-data/service-salary/index.vue'),
  },
  {
    path: "/master-data/bank",
    name: "bank",
    meta: {
      title: "Bank",
      authRequired: true,
    },
    component: () => import("../views/master-data/bank/index.vue"),
  },

  /* -------------------------------------------------------------------------- */
  /*                                  SETTINGS                                  */
  /* -------------------------------------------------------------------------- */
  {
    path: "/settings/reason",
    name: "reason",
    meta: {
      title: "Kebijakan Alasan Stop",
      authRequired: true,
    },
    component: () => import("../views/settings/reason/index.vue"),
  },
  {
    path: "/settings/transportation",
    name: "transportation",
    meta: {
      title: "Kebijakan Transportasi",
      authRequired: true,
    },
    component: () => import("../views/settings/transportation/index.vue"),
  },
  {
    path: "/settings/payroll-schedule",
    name: "payroll-schedule",
    meta: {
      title: "Kebijakan Jadwal Penggajian",
      authRequired: true,
    },
    component: () => import("../views/settings/payroll-schedule/index.vue"),
  },
  {
    path: "/settings/attendance-schedule",
    name: "attendance-schedule",
    meta: {
      title: "Kebijakan Jadwal Absensi",
      authRequired: true,
    },
    component: () => import("../views/settings/attendance-schedule/index.vue"),
  },
  {
    path: "/settings/time-off",
    name: "time-off",
    meta: {
      title: "Kebijakan Kehadiran",
      authRequired: true,
    },
    component: () => import("../views/settings/time-off/index.vue"),
  },
  {
    path: "/settings/compensation",
    name: "compensation",
    meta: {
      title: "Kebijakan Kompensasi",
      authRequired: true,
    },
    component: () => import("../views/settings/compensation/index.vue"),
  },

  /* -------------------------------------------------------------------------- */
  /*                                 ATTENDANCE                                 */
  /*                                                                            */
  /* Modul Absensi. Fitur "Monitoring Absensi" memantau kehadiran SELURUH       */
  /* karyawan dalam satu halaman, dipisah per organisasi memakai tab pane       */
  /* lokal (Kantor / Visit / Perawat) — bukan nested route.                     */
  /* -------------------------------------------------------------------------- */
  {
    path: "/attendance/monitoring",
    name: "attendance-monitoring",
    meta: {
      title: "Monitoring Absensi",
      authRequired: true,
    },
    component: () => import("../views/attendance/monitoring/index.vue"),
  },
  {
    path: "/attendance/request-time-off",
    name: "attendance-request-time-off",
    meta: {
      title: "Perizinan",
      authRequired: true,
    },
    component: () =>
      import("../views/attendance/request-time-off/index.vue"),
  },
  /* -------------------------------------------------------------------------- */
  /* Fitur "Pengajuan Lupa Absensi" (submission) — memvalidasi pengajuan        */
  /* karyawan yang lupa clock in/out. Dipisah per organisasi memakai tab pane   */
  /* lokal (Kantor / Visit / Perawat). Saat ini baru organisasi Office (Kantor) */
  /* yang memiliki API; Visit & Perawat masih placeholder.                      */
  /* -------------------------------------------------------------------------- */
  {
    path: "/attendance/submission",
    name: "attendance-submission",
    meta: {
      title: "Pengajuan Lupa Absensi",
      authRequired: true,
    },
    component: () => import("../views/attendance/submission/index.vue"),
  },

  /* -------------------------------------------------------------------------- */
  /*                                  PERSONNEL                                 */
  /* -------------------------------------------------------------------------- */
  {
    path: "/personnel/employee",
    name: "employee",
    meta: {
      title: "Karyawan",
      authRequired: true,
    },
    component: () => import("../views/personnel/employee/index.vue"),
  },
  {
    path: "/personnel/employee/create",
    name: "employee-create",
    meta: {
      title: "Tambah Karyawan",
      authRequired: true,
    },
    component: () => import("../views/personnel/employee/create/index.vue"),
  },
  {
    path: "/personnel/employee/onboarding",
    name: "employee-onboarding",
    meta: {
      title: "Pendaftaran Link",
      authRequired: true,
    },
    component: () => import("../views/personnel/employee/onboarding/index.vue"),
  },
  {
    path: "/personnel/credential",
    name: "credential",
    meta: {
      title: "Kredensial",
      authRequired: true,
    },
    component: () => import("../views/personnel/credential/index.vue"),
  },
  {
    path: "/personnel/career",
    name: "career",
    meta: {
      title: "Karir",
      authRequired: true,
    },
    component: () => import("../views/personnel/career/index.vue"),
  },
  {
    path: "/personnel/applicant",
    name: "applicant",
    meta: {
      title: "Pelamar",
      authRequired: true,
    },
    component: () => import("../views/personnel/applicant/index.vue"),
  },
  {
    path: "/personnel/job-position",
    name: "job-position",
    meta: {
      title: "Jabatan",
      authRequired: true,
    },
    component: () => import("../views/personnel/job-position/index.vue"),
  },
  {
    path: "/personnel/division",
    name: "division",
    meta: {
      title: "Divisi",
      authRequired: true,
    },
    component: () => import("../views/personnel/division/index.vue"),
  },
  {
    path: "/personnel/warning-letter",
    name: "warning-letter",
    meta: {
      title: "Surat Peringatan",
      authRequired: true,
    },
    component: () => import("../views/personnel/warning-letter/index.vue"),
  },
  /* ----------------------------------------------------------------------- */
  /*                       DETAIL KARYAWAN (per organisasi)                  */
  /*                                                                         */
  /* Detail Karyawan dipisah per organisasi: Officer / Homecare / Visitor.   */
  /* Tiap organisasi punya halaman shell sendiri (header + nav section) dan  */
  /* sekumpulan NESTED CHILD ROUTE per section (Profil, Absensi, dst).       */
  /*                                                                         */
  /* Pola nested route dipilih agar tiap section bisa di-deep-link & punya   */
  /* tombol back browser; shell (header + tab) tetap mount saat pindah       */
  /* section. Sub-tab ringan DI DALAM section (mis. Data Karyawan /          */
  /* Pendidikan / Pengalaman / Kontak Darurat) tetap tab-pane lokal.         */
  /*                                                                         */
  /* Saat ini baru Officer yang diimplementasi (statis). Homecare & Visitor  */
  /* menyusul dengan pola folder yang sama (detail/homecare, detail/visitor).*/
  /* ----------------------------------------------------------------------- */
  {
    path: "/personnel/employee/officer/:id",
    component: () => import("../views/personnel/employee/detail/officer/index.vue"),
    meta: {
      title: "Detail Karyawan",
      authRequired: true,
    },
    // redirect default ke section Profil
    redirect: { name: "employee-detail-officer-profile" },
    children: [
      {
        path: "profil",
        name: "employee-detail-officer-profile",
        meta: { title: "Detail Karyawan", authRequired: true },
        component: () =>
          import(
            "../views/personnel/employee/detail/officer/sections/ProfileSection.vue"
          ),
      },
      {
        path: "absensi",
        name: "employee-detail-officer-attendance",
        meta: { title: "Detail Karyawan", authRequired: true },
        component: () =>
          import(
            "../views/personnel/employee/detail/officer/sections/AttendanceSection.vue"
          ),
      },
      {
        path: "kontrak",
        name: "employee-detail-officer-contract",
        meta: { title: "Detail Karyawan", authRequired: true },
        component: () =>
          import(
            "../views/personnel/employee/detail/officer/sections/ContractSection.vue"
          ),
      },
      {
        path: "payroll",
        name: "employee-detail-officer-payroll",
        meta: { title: "Detail Karyawan", authRequired: true },
        component: () =>
          import(
            "../views/personnel/employee/detail/officer/sections/PayrollSection.vue"
          ),
      },
      {
        path: "perizinan",
        name: "employee-detail-officer-permission",
        meta: { title: "Detail Karyawan", authRequired: true },
        component: () =>
          import(
            "../views/personnel/employee/detail/officer/sections/PermissionSection.vue"
          ),
      },
      {
        path: "sp",
        name: "employee-detail-officer-warning",
        meta: { title: "Detail Karyawan", authRequired: true },
        component: () =>
          import(
            "../views/personnel/employee/detail/officer/sections/WarningSection.vue"
          ),
      },
    ],
  },

  /* ----------------------------------------------------------------------- */
  /* Detail Karyawan — Visitor                                               */
  /*                                                                         */
  /* Pola folder & route sama dengan Officer (shell + child route per        */
  /* section). Saat ini baru section Profil yang diimplementasi; section     */
  /* lain pakai SectionPlaceholder (di-reuse dari folder officer).           */
  /*                                                                         */
  /* Profil Visitor punya sub-tab tambahan "Kredensial" (select2 multiple). */
  /* ----------------------------------------------------------------------- */
  {
    path: "/personnel/employee/visitor/:id",
    component: () => import("../views/personnel/employee/detail/visitor/index.vue"),
    meta: {
      title: "Detail Karyawan",
      authRequired: true,
    },
    redirect: { name: "employee-detail-visitor-profile" },
    children: [
      {
        path: "profil",
        name: "employee-detail-visitor-profile",
        meta: { title: "Detail Karyawan", authRequired: true },
        component: () =>
          import(
            "../views/personnel/employee/detail/visitor/sections/ProfileSection.vue"
          ),
      },
      {
        path: "absensi",
        name: "employee-detail-visitor-attendance",
        meta: { title: "Detail Karyawan", authRequired: true },
        component: () =>
          import(
            "../views/personnel/employee/detail/visitor/sections/AttendanceSection.vue"
          ),
      },
      {
        path: "kontrak",
        name: "employee-detail-visitor-contract",
        meta: { title: "Detail Karyawan", authRequired: true },
        // Kontrak Visitor identik dengan Officer → reuse section officer
        // (self-contained, hanya butuh prop employeeId dari router-view).
        component: () =>
          import(
            "../views/personnel/employee/detail/officer/sections/ContractSection.vue"
          ),
      },
      {
        path: "payroll",
        name: "employee-detail-visitor-payroll",
        meta: { title: "Detail Karyawan", authRequired: true },
        component: () =>
          import(
            "../views/personnel/employee/detail/visitor/sections/PayrollSection.vue"
          ),
      },
      {
        path: "perizinan",
        name: "employee-detail-visitor-permission",
        meta: { title: "Detail Karyawan", authRequired: true },
        // Perizinan Visitor identik dengan Officer → reuse section officer.
        component: () =>
          import(
            "../views/personnel/employee/detail/officer/sections/PermissionSection.vue"
          ),
      },
      {
        path: "sp",
        name: "employee-detail-visitor-warning",
        meta: { title: "Detail Karyawan", authRequired: true },
        // SP (Surat Peringatan) Visitor identik dengan Officer → reuse section officer.
        component: () =>
          import(
            "../views/personnel/employee/detail/officer/sections/WarningSection.vue"
          ),
      },
      {
        path: "komplain",
        name: "employee-detail-visitor-complaint",
        meta: { title: "Detail Karyawan", authRequired: true },
        component: () =>
          import(
            "../views/personnel/employee/detail/visitor/sections/ComplaintSection.vue"
          ),
      },
    ],
  },

  /* ----------------------------------------------------------------------- */
  /* Detail Karyawan — Homecare                                              */
  /*                                                                         */
  /* Pola folder & route sama dengan Officer/Visitor (shell + child route    */
  /* per section). Perbedaan khas Homecare:                                  */
  /*  - Profil punya sub-tab "Kredensial" (opsi master organization=homecare)*/
  /*  - Absensi memakai endpoint homecare-attendance (Absensi Perawat)       */
  /*  - Payroll sub-tab "Slip Gaji" memakai endpoint payroll/homecare/*      */
  /*  - Perizinan memakai endpoint nurse-leave (Perizinan Perawat)           */
  /*  - Komplain di-reuse penuh dari Visitor (ComplaintSection visitor).     */
  /*                                                                         */
  /* Kontrak & SP di-reuse penuh dari Officer (identik lintas-organisasi).   */
  /* ----------------------------------------------------------------------- */
  {
    path: "/personnel/employee/homecare/:id",
    component: () => import("../views/personnel/employee/detail/homecare/index.vue"),
    meta: {
      title: "Detail Karyawan",
      authRequired: true,
    },
    redirect: { name: "employee-detail-homecare-profile" },
    children: [
      {
        path: "profil",
        name: "employee-detail-homecare-profile",
        meta: { title: "Detail Karyawan", authRequired: true },
        component: () =>
          import(
            "../views/personnel/employee/detail/homecare/sections/ProfileSection.vue"
          ),
      },
      {
        path: "absensi",
        name: "employee-detail-homecare-attendance",
        meta: { title: "Detail Karyawan", authRequired: true },
        component: () =>
          import(
            "../views/personnel/employee/detail/homecare/sections/AttendanceSection.vue"
          ),
      },
      {
        path: "kontrak",
        name: "employee-detail-homecare-contract",
        meta: { title: "Detail Karyawan", authRequired: true },
        // Kontrak Homecare identik dengan Officer → reuse section officer.
        component: () =>
          import(
            "../views/personnel/employee/detail/officer/sections/ContractSection.vue"
          ),
      },
      {
        path: "payroll",
        name: "employee-detail-homecare-payroll",
        meta: { title: "Detail Karyawan", authRequired: true },
        component: () =>
          import(
            "../views/personnel/employee/detail/homecare/sections/PayrollSection.vue"
          ),
      },
      {
        path: "perizinan",
        name: "employee-detail-homecare-permission",
        meta: { title: "Detail Karyawan", authRequired: true },
        // Perizinan Perawat Homecare — endpoint nurse-leave (filter rentang
        // tanggal + kartu kuota statis). Berbeda dari Perizinan Officer.
        component: () =>
          import(
            "../views/personnel/employee/detail/homecare/sections/PermissionSection.vue"
          ),
      },
      {
        path: "sp",
        name: "employee-detail-homecare-warning",
        meta: { title: "Detail Karyawan", authRequired: true },
        // SP (Surat Peringatan) Homecare identik dengan Officer → reuse section officer.
        component: () =>
          import(
            "../views/personnel/employee/detail/officer/sections/WarningSection.vue"
          ),
      },
      {
        path: "komplain",
        name: "employee-detail-homecare-complaint",
        meta: { title: "Detail Karyawan", authRequired: true },
        // Komplain Homecare identik dengan Visitor → reuse section visitor
        // (self-contained: maps + komponen komplain ada di folder visitor;
        //  endpoint complaint berbasis user_id, lintas-organisasi).
        component: () =>
          import(
            "../views/personnel/employee/detail/visitor/sections/ComplaintSection.vue"
          ),
      },
    ],
  },

  /* -------------------------------------------------------------------------- */
  /*                            PELAYANAN (SERVICE)                             */
  /* -------------------------------------------------------------------------- */
  {
    // Pasien & Penanggung Jawab
    path: "/service/patient-customer",
    name: "service-patient-customer",
    meta: {
      title: "Pasien dan Penanggung Jawab",
      authRequired: true,
    },
    component: () =>
      import("../views/service/patient-customer/index.vue"),
  },
  {
    // Detail Pasien
    path: "/service/patient/:id",
    name: "service-patient-detail",
    meta: {
      title: "Detail Pasien",
      authRequired: true,
    },
    component: () =>
      import("../views/service/patient-customer/detail/index.vue"),
  },
  {
    // Detail Penanggung Jawab
    path: "/service/customer/:id",
    name: "service-customer-detail",
    meta: {
      title: "Detail Penanggung Jawab",
      authRequired: true,
    },
    component: () =>
      import("../views/service/patient-customer/customer-detail/index.vue"),
  },
  {
    // Invoice
    path: "/service/invoice",
    name: "invoice",
    meta: {
      title: "Invoice",
      authRequired: true,
    },
    component: () => import("../views/service/invoice/index.vue"),
  },
  {
    // Invoice — Buat Invoice (form tambah; statis, API menyusul)
    path: "/service/invoice/create",
    name: "invoice-create",
    meta: {
      title: "Buat Invoice",
      authRequired: true,
    },
    component: () => import("../views/service/invoice/create/index.vue"),
  },

  // ==============================
  // TODO: Tambahkan route fitur Portal IHC lainnya di sini
  // ==============================
  // routes.js — paling bawah!
  {
    path: "/:pathMatch(.*)*",
    name: "not-found",
    component: () => import("../views/errors/404.vue"),
  },
];